import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import logo from '@/assets/images/logo/logo.svg';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { PasswordInput } from '@/components/ui/password-input';
import { getBasename } from '@/utils/url';
import { useI18n } from '@/contexts/I18nContext';

const TOKEN_KEY = 'sossiety_reset_token';
const RECOVERY_FLAG = 'sossiety_password_recovery';

export default function ResetPassword() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [canShowForm, setCanShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const queryParams = new URLSearchParams(window.location.search.substring(1));
      const tokenParam = hashParams.get("token") || hashParams.get("access_token") || queryParams.get("token") || queryParams.get("access_token");
      const type = hashParams.get("type") || queryParams.get("type");
      if (type === "recovery" && tokenParam) {
        setToken(tokenParam);
        setCanShowForm(true);
        supabase.auth.exchangeCodeForSession(tokenParam);
        localStorage.setItem(TOKEN_KEY, tokenParam);
        localStorage.setItem(RECOVERY_FLAG, '1');
      } else if (localStorage.getItem(RECOVERY_FLAG) === '1') {
        setCanShowForm(true);
      }
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    setLoading(true);
    if (password !== confirmPassword) {
      setError(t('reset_password.passwords_not_match'));
        setLoading(false);
        return;
      }
    if (password.length < 6) {
      setError(t('reset_password.password_min_length'));
        setLoading(false);
        return;
      }
    const { error } = await supabase.auth.updateUser({ password });
      if (error) {
      setError(error.message);
      } else {
      setSuccess(true);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(RECOVERY_FLAG);
      setTimeout(() => {
        const basename = getBasename();
        const cleanBasename = (basename === '/' || basename === '') ? '' : basename;
        window.location.href = `${cleanBasename}/`;
      }, 2000);
    }
    setLoading(false);
  };

  // Password validation helpers
  const hasMinLength = password.length >= 8;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const isPasswordValid = hasMinLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
  const passwordsMatch = password === confirmPassword;

  if (!canShowForm && !user) {
    return <div className="min-h-screen flex items-center justify-center text-center text-lg text-red-600">{t('reset_password.invalid_link')}</div>;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 pt-20 pb-24 bg-[#160C0C]">
      <TopNavigation />
      <div className="w-full max-w-md mt-2 mb-4 pl-2">
        <Link 
          to="/" 
          className="text-white/80 hover:text-white font-sans text-sm transition-colors duration-200 flex items-center gap-2 underline"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('reset_password.back_to_signin')}
        </Link>
      </div>
      <div className="w-full max-w-md flex-1 flex flex-col justify-center items-center">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-2">
            <img src={logo} alt="sOSSiety logo" className="h-12" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2 font-sans">{t('reset_password.title')}</h2>
          <p className="text-white/80">{t('reset_password.description')}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <PasswordInput
              placeholder={t('reset_password.new_password_placeholder')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            {/* Password requirements */}
            <div className="flex flex-wrap gap-1 text-xs text-white/70 mt-2">
              <div className="flex items-center gap-1 px-2 py-1 bg-black/40 rounded">
                {hasMinLength ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <X className="w-4 h-4 text-red-500" />
                )}
                <span>{t('reset_password.password_requirements.min_length')}</span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-black/40 rounded">
                {hasSpecialChar ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <X className="w-4 h-4 text-red-500" />
                )}
                <span>{t('reset_password.password_requirements.special_char')}</span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-black/40 rounded">
                {hasUpperCase ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <X className="w-4 h-4 text-red-500" />
                )}
                <span>{t('reset_password.password_requirements.uppercase')}</span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-black/40 rounded">
                {hasLowerCase ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <X className="w-4 h-4 text-red-500" />
                )}
                <span>{t('reset_password.password_requirements.lowercase')}</span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-black/40 rounded">
                {hasNumber ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <X className="w-4 h-4 text-red-500" />
                )}
                <span>{t('reset_password.password_requirements.number')}</span>
              </div>
            </div>
          </div>
          <div className="relative">
            <PasswordInput
              placeholder={t('reset_password.confirm_new_password_placeholder')}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
            {/* Passwords do not match warning */}
            {confirmPassword && !passwordsMatch && (
              <p className="mt-1 text-sm text-white">{t('reset_password.passwords_not_match')}</p>
            )}
          </div>
          {error && <div className="text-red-400 bg-red-900/30 border border-red-500 rounded-lg px-4 py-2 text-sm">{error}</div>}
          {success && <div className="text-green-400 bg-green-900/30 border border-[#2ECC71] rounded-lg px-4 py-2 text-sm" style={{color:'#2ECC71',borderColor:'#2ECC71',background:'#2ecc7133'}}>{t('reset_password.password_updated')}</div>}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-sossiety-red hover:bg-sossiety-red-hover text-white font-medium py-4 h-14 rounded-full transition-colors duration-200 disabled:opacity-50"
          >
            {loading ? t('reset_password.sending') : t('reset_password.send_email')}
          </Button>
        </form>
      </div>
      <BottomNavigation />
    </div>
  );
}
